import { useState } from 'react';

import { useSend, type SendKind } from './useSend.js';

interface SendFormProps {
  kind: SendKind;
  course: {
    id: string;
    name: string;
    provider_name?: string;
    cricos_code?: string | null;
  };
  onClose: () => void;
  onSuccess?: (result: { id: string; messageId: string }) => void;
}

/**
 * Three-field send form: recipient name + email, plus an optional
 * "personalise message" expander for subject + body overrides.
 *
 * Visual structure mirrors docs/06_BRAND_AND_UI.md modal conventions:
 *   header (with course meta) → body (form) → footer (cancel + send).
 */
export function SendForm({ kind, course, onClose, onSuccess }: SendFormProps) {
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [showPersonalise, setShowPersonalise] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const mutation = useSend(kind);

  function validate(): boolean {
    setLocalError(null);
    if (!recipientEmail.trim()) {
      setLocalError('Recipient email is required');
      return false;
    }
    // Light client-side check; server does the strict Zod validation.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim())) {
      setLocalError('That email address looks malformed');
      return false;
    }
    return true;
  }

  async function onSubmit() {
    if (!validate()) return;
    try {
      const result = await mutation.mutateAsync({
        courseId: course.id,
        recipientEmail,
        recipientName: recipientName || undefined,
        subjectOverride: showPersonalise && subject ? subject : undefined,
        bodyOverride: showPersonalise && body ? body : undefined,
      });
      if (onSuccess) onSuccess({ id: result.id, messageId: result.messageId });
    } catch (err) {
      // mutation.error already set; render below
      console.error('Send failed', err);
    }
  }

  const title = kind === 'overview' ? 'Send course overview' : 'Send application checklist';
  const cta = kind === 'overview' ? 'Send overview' : 'Send checklist';

  // --- success state -------------------------------------------------------
  if (mutation.isSuccess) {
    return (
      <div className="flex flex-1 flex-col min-h-0">
        <Header title={title} subtitle={`${course.name}${course.provider_name ? ` · ${course.provider_name}` : ''}`} />
        <div className="flex-1 overflow-y-auto px-7 py-8">
          <div
            className="rounded-md px-4 py-3 text-sm"
            style={{ backgroundColor: '#E8F3EC', color: '#1E7A47', border: '1px solid #C9E5D2' }}
          >
            <strong>Sent.</strong> A copy with the branded PDF is on its way to{' '}
            <span style={{ fontFamily: 'ui-monospace, SF Mono, Menlo, monospace' }}>{recipientEmail}</span>.
          </div>
          <p className="mt-4 text-[13px]" style={{ color: '#6B6B6B' }}>
            Message ID:{' '}
            <span style={{ fontFamily: 'ui-monospace, SF Mono, Menlo, monospace', color: '#0D2B4A' }}>
              {mutation.data.messageId}
            </span>
          </p>
        </div>
        <Footer>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-[13.5px] font-medium"
            style={{ backgroundColor: '#0D2B4A', color: '#FFFFFF' }}
          >
            Done
          </button>
        </Footer>
      </div>
    );
  }

  // --- form state ----------------------------------------------------------
  return (
    <div className="flex flex-1 flex-col min-h-0">
      <Header
        title={title}
        subtitle={`${course.name}${course.provider_name ? ` · ${course.provider_name}` : ''}${course.cricos_code ? ` · CRICOS ${course.cricos_code}` : ''}`}
      />

      <div className="flex-1 overflow-y-auto px-7 py-5 space-y-4">
        {/* Recipient name */}
        <Field label="Recipient name (optional)">
          <input
            type="text"
            value={recipientName}
            onChange={(e) => setRecipientName(e.target.value)}
            placeholder="Raj K"
            disabled={mutation.isPending}
            className="block w-full rounded-md px-3 py-2 text-[14px] outline-none transition"
            style={{
              border: '1px solid #E8E2D5',
              backgroundColor: '#FFFFFF',
              color: '#0F0F0F',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = '#0D2B4A')}
            onBlur={(e) => (e.currentTarget.style.borderColor = '#E8E2D5')}
          />
        </Field>

        {/* Recipient email */}
        <Field label="Recipient email" required>
          <input
            type="email"
            value={recipientEmail}
            onChange={(e) => setRecipientEmail(e.target.value)}
            placeholder="raj.k@example.com"
            disabled={mutation.isPending}
            required
            autoFocus
            className="block w-full rounded-md px-3 py-2 text-[14px] outline-none transition"
            style={{
              border: '1px solid #E8E2D5',
              backgroundColor: '#FFFFFF',
              color: '#0F0F0F',
              fontFamily: 'ui-monospace, SF Mono, Menlo, monospace',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = '#0D2B4A')}
            onBlur={(e) => (e.currentTarget.style.borderColor = '#E8E2D5')}
          />
        </Field>

        {/* Personalise expander */}
        <div>
          <button
            type="button"
            onClick={() => setShowPersonalise(!showPersonalise)}
            disabled={mutation.isPending}
            className="text-[13px] font-medium"
            style={{ color: '#0D2B4A', background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
          >
            {showPersonalise ? '− Hide' : '+ Personalise'} subject &amp; message
          </button>

          {showPersonalise && (
            <div className="mt-3 space-y-3">
              <Field label="Subject (optional)">
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={`Course overview — ${course.name}`}
                  disabled={mutation.isPending}
                  className="block w-full rounded-md px-3 py-2 text-[14px] outline-none transition"
                  style={{ border: '1px solid #E8E2D5', backgroundColor: '#FFFFFF', color: '#0F0F0F' }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = '#0D2B4A')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = '#E8E2D5')}
                />
              </Field>
              <Field label="Replace default message (optional)">
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Write a personal note that replaces the default opening paragraph…"
                  rows={5}
                  disabled={mutation.isPending}
                  className="block w-full rounded-md px-3 py-2 text-[14px] outline-none transition resize-y"
                  style={{
                    border: '1px solid #E8E2D5',
                    backgroundColor: '#FFFFFF',
                    color: '#0F0F0F',
                    fontFamily: 'inherit',
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = '#0D2B4A')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = '#E8E2D5')}
                />
              </Field>
            </div>
          )}
        </div>

        {/* Error display */}
        {(localError || mutation.error) && (
          <div
            className="rounded-md px-3 py-2 text-[13px]"
            style={{ backgroundColor: '#FBEAEA', color: '#A83232', border: '1px solid #F0D0D0' }}
          >
            {localError ?? readError(mutation.error)}
          </div>
        )}

        {/* What gets sent — info row */}
        <div
          className="rounded-md px-3 py-2 text-[12px]"
          style={{ backgroundColor: '#FBF8F2', color: '#6B6B6B', border: '1px solid #E8E2D5' }}
        >
          A branded PDF{' '}
          <span style={{ color: '#0D2B4A', fontWeight: 500 }}>
            ({kind === 'overview' ? 'course-overview.pdf' : 'application-checklist.pdf'})
          </span>{' '}
          will be attached. A copy of the send is logged in your audit trail.
        </div>
      </div>

      <Footer>
        <button
          type="button"
          onClick={onClose}
          disabled={mutation.isPending}
          className="rounded-md px-4 py-2 text-[13.5px] font-medium"
          style={{ backgroundColor: 'transparent', color: '#2A2A2A', border: 'none' }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={mutation.isPending || !recipientEmail.trim()}
          className="rounded-md px-5 py-2 text-[13.5px] font-medium"
          style={{
            backgroundColor: mutation.isPending ? '#9B9B9B' : '#C8A24B',
            color: '#0D2B4A',
            opacity: !recipientEmail.trim() && !mutation.isPending ? 0.6 : 1,
            cursor: mutation.isPending || !recipientEmail.trim() ? 'not-allowed' : 'pointer',
          }}
        >
          {mutation.isPending ? 'Sending…' : cta}
        </button>
      </Footer>
    </div>
  );
}

// ----- small layout primitives ---------------------------------------------

function Header({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div className="px-7 pt-6 pb-4" style={{ borderBottom: '1px solid #E8E2D5' }}>
      <h2
        style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontWeight: 500,
          color: '#0D2B4A',
          fontSize: '20px',
          margin: 0,
          lineHeight: 1.25,
          letterSpacing: '-0.01em',
        }}
      >
        {title}
      </h2>
      <div className="mt-1 text-[13px]" style={{ color: '#6B6B6B' }}>
        {subtitle}
      </div>
    </div>
  );
}

function Footer({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="px-7 py-4 flex items-center justify-end gap-2"
      style={{ borderTop: '1px solid #E8E2D5', backgroundColor: '#FBF8F2', borderBottomLeftRadius: 14, borderBottomRightRadius: 14 }}
    >
      {children}
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div
        className="mb-1.5 text-[11px] font-medium"
        style={{
          color: '#6B6B6B',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {label}
        {required && <span style={{ color: '#A83232', marginLeft: 4 }}>*</span>}
      </div>
      {children}
    </label>
  );
}

function readError(err: unknown): string {
  if (!err) return 'Something went wrong';
  if (err instanceof Error) return err.message;
  return String(err);
}
